import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { MentionSentiment } from 'src/interfaces/topics.interface'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import {
  TopicsRangeParams,
  socialTopicsApi,
} from 'src/services/socialTopicsApi'

/**
 * The three Topics reads.
 *
 * Each is keyed by campaign AND window, so switching the date picker refetches
 * rather than showing the previous window's numbers under the new label.
 *
 * Every query waits for `fromDate`. The pages derive their window from the URL,
 * which is empty on the first render and filled a tick later — without this
 * gate each widget fires once with no window and again with one, doubling the
 * load on a 273k-document collection for a result that is thrown away.
 *
 * The detail query additionally waits for a topic: the modal mounts with no
 * selection and would otherwise request an empty topic on every page view.
 */

export const useGetTopicsOverviewQuery = (
  campaignId?: string,
  params?: TopicsRangeParams
) =>
  useQuery({
    enabled: !!campaignId && !!params?.fromDate,
    queryFn: () => socialTopicsApi.getOverview(campaignId as string, params),
    queryKey: [
      QueryKey.GET_TOPICS_OVERVIEW,
      campaignId,
      params?.fromDate,
      params?.toDate,
    ],
  })

/**
 * Overview across the campaigns in view on the campaigns page. Keyed by the
 * sorted id list so reordering the same selection does not refetch, while
 * adding or removing a campaign does.
 */
export const useGetTopicsOverviewForCampaignsQuery = (
  campaignIds: string[],
  params?: TopicsRangeParams
) => {
  const key = [...campaignIds].sort().join(',')
  return useQuery({
    enabled: campaignIds.length > 0 && !!params?.fromDate,
    queryFn: () => socialTopicsApi.getOverviewForCampaigns(campaignIds, params),
    queryKey: [
      QueryKey.GET_TOPICS_OVERVIEW,
      'campaigns',
      key,
      params?.fromDate,
      params?.toDate,
    ],
  })
}

export const useGetTopicsTableQuery = (
  campaignId?: string,
  params?: TopicsRangeParams
) =>
  useQuery({
    enabled: !!campaignId && !!params?.fromDate,
    queryFn: () => socialTopicsApi.getTable(campaignId as string, params),
    queryKey: [
      QueryKey.GET_TOPICS_TABLE,
      campaignId,
      params?.fromDate,
      params?.toDate,
    ],
  })

export const useGetTopicDetailQuery = (
  campaignId?: string,
  topic?: string | null,
  params?: TopicsRangeParams,
  // Narrows the read to one subtopic: the modal's drill-down. Part of the
  // key so topic and subtopic views cache separately.
  subtopic?: string | null,
  // The Related Mentions tab: narrows the posts list alone.
  sentiment?: MentionSentiment | null
) =>
  useQuery({
    enabled: !!campaignId && !!topic && !!params?.fromDate,
    // Switching tab or drilling down keeps the modal filled instead of
    // flashing its skeleton; the modal hides what does not match its view.
    placeholderData: keepPreviousData,
    queryFn: () =>
      socialTopicsApi.getDetail(campaignId as string, topic as string, {
        ...params,
        ...(subtopic ? { subtopic } : {}),
        ...(sentiment ? { sentiment } : {}),
      }),
    queryKey: [
      QueryKey.GET_TOPIC_DETAIL,
      campaignId,
      topic,
      subtopic ?? null,
      sentiment ?? null,
      params?.fromDate,
      params?.toDate,
    ],
  })
